import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Plus, Edit3, Trash2, Upload, Building2, Copy, Users, Star, LogIn } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { id: null, nome: '', codice_univoco: '', descrizione: '', top_agent_id: '', logo_url: null, target_margin_nuovo: 4.00, target_margin_usato: 2.00, target_margin_4vantage: 2.00 };

const ConvenzioniAdminPage = () => {
  const { getAuthHeader, user, enterConvenzione, activeConvenzione } = useAuth();
  const navigate = useNavigate();
  const [convenzioni, setConvenzioni] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, mode: 'create', form: emptyForm });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(null);
  // Fase 1.4: badge iscritti in attesa per singola convenzione { conv_id: count }
  const [pendingByConv, setPendingByConv] = useState({});

  const isSuperAdmin = user?.role === 'super_admin';

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [convRes, usersRes, pendingRes] = await Promise.all([
        axios.get(`${API}/admin/convenzioni`, { headers: getAuthHeader() }),
        axios.get(`${API}/users`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/convenzioni-pending/counts`, { headers: getAuthHeader() }).catch(() => ({ data: { by_convenzione: {} } })),
      ]);
      setConvenzioni(convRes.data || []);
      // 2.6.5 — Include ANCHE gli agenti semplici (backend UserRole.AGENT = 'agente' in italiano).
      // Precedentemente il filtro conteneva 'agent' (inglese) che escludeva gli agenti dalla lista referenti.
      const topAgents = (usersRes.data || []).filter(u =>
        ['agente', 'agent', 'top_agent', 'admin', 'super_admin'].includes(u.role) && u.is_active !== false
      );
      setAgents(topAgents);
      setPendingByConv(pendingRes.data?.by_convenzione || {});
    } catch {
      toast.error('Errore caricamento convenzioni');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => setDialog({ open: true, mode: 'create', form: { ...emptyForm } });
  const openEdit = (c) => setDialog({
    open: true,
    mode: 'edit',
    form: {
      id: c.id,
      nome: c.nome || '',
      codice_univoco: c.codice_univoco || '',
      descrizione: c.descrizione || '',
      top_agent_id: c.top_agent_id || '',
      logo_url: c.logo_url,
      is_default: c.is_default,
      target_margin_nuovo: c.target_margin_nuovo ?? 4.00,
      target_margin_usato: c.target_margin_usato ?? 2.00,
      target_margin_4vantage: c.target_margin_4vantage ?? 2.00,
    },
  });
  const closeDialog = () => setDialog({ ...dialog, open: false });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        nome: dialog.form.nome,
        descrizione: dialog.form.descrizione,
        target_margin_nuovo: dialog.form.target_margin_nuovo != null && dialog.form.target_margin_nuovo !== '' ? Number(dialog.form.target_margin_nuovo) : undefined,
        target_margin_usato: dialog.form.target_margin_usato != null && dialog.form.target_margin_usato !== '' ? Number(dialog.form.target_margin_usato) : undefined,
        target_margin_4vantage: dialog.form.target_margin_4vantage != null && dialog.form.target_margin_4vantage !== '' ? Number(dialog.form.target_margin_4vantage) : undefined,
      };
      if (dialog.form.codice_univoco) payload.codice_univoco = dialog.form.codice_univoco;
      if (dialog.form.top_agent_id) payload.top_agent_id = dialog.form.top_agent_id;
      else if (dialog.mode === 'edit') payload.clear_top_agent = true;

      if (dialog.mode === 'create') {
        await axios.post(`${API}/admin/convenzioni`, payload, { headers: getAuthHeader() });
        toast.success('Convenzione creata');
      } else {
        await axios.put(`${API}/admin/convenzioni/${dialog.form.id}`, payload, { headers: getAuthHeader() });
        toast.success('Convenzione aggiornata');
      }
      closeDialog();
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Confermi eliminazione? Non è reversibile.')) return;
    try {
      await axios.delete(`${API}/admin/convenzioni/${id}`, { headers: getAuthHeader() });
      toast.success('Convenzione eliminata');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const handleLogoUpload = async (convenzioneId, file) => {
    if (!file) return;
    setUploadingLogo(convenzioneId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/admin/convenzioni/${convenzioneId}/logo`, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Logo caricato');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore upload logo');
    } finally {
      setUploadingLogo(null);
    }
  };

  const copyCodice = (codice) => {
    navigator.clipboard.writeText(codice);
    toast.success('Codice copiato');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="convenzioni-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Gestione Convenzioni
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Convenzioni aziendali/associazioni con codice univoco per l&apos;accesso cliente
          </p>
        </div>
        {isSuperAdmin && (
          <Button onClick={openCreate} data-testid="btn-nuova-convenzione" className="gap-1">
            <Plus className="w-4 h-4" /> Nuova Convenzione
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {convenzioni.map(c => (
          <Card key={c.id} data-testid={`convenzione-card-${c.id}`} className={`relative ${c.is_default ? 'border-blue-300 bg-blue-50/30' : ''}`}>
            {(pendingByConv[c.id] || 0) > 0 && (
              <div
                className="absolute -top-2 -right-2 min-w-[28px] h-7 px-2 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center shadow-md animate-pulse"
                title={`${pendingByConv[c.id]} iscritti in attesa di approvazione`}
                data-testid={`pending-badge-${c.id}`}
              >
                {pendingByConv[c.id] > 99 ? '99+' : pendingByConv[c.id]}
              </div>
            )}
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg flex items-center gap-2">
                    {c.nome}
                    {c.is_default && <Badge className="bg-blue-100 text-blue-700 text-[10px]">DEFAULT</Badge>}
                  </CardTitle>
                  <CardDescription className="text-xs mt-1 flex items-center gap-1">
                    Codice: <span className="font-mono font-semibold text-slate-800">{c.codice_univoco}</span>
                    <button onClick={() => copyCodice(c.codice_univoco)} className="text-slate-400 hover:text-slate-700">
                      <Copy className="w-3 h-3" />
                    </button>
                  </CardDescription>
                </div>
                {c.logo_url && (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${c.logo_url}`} alt="logo" className="max-h-12 max-w-[80px] object-contain" />
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {c.descrizione && <p className="text-xs text-slate-600">{c.descrizione}</p>}
              <div className="flex items-center gap-2 text-xs text-slate-500">
                {c.top_agent ? (
                  <span className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-500" /> Referente: <strong>{c.top_agent.name}</strong></span>
                ) : (
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" /> Sede diretta</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                <Button
                  size="sm"
                  onClick={() => {
                    enterConvenzione({ id: c.id, nome: c.nome, codice_univoco: c.codice_univoco });
                    toast.success(`Entrato in "${c.nome}". La Rubrica e le Offerte mostrano solo i dati della convenzione.`);
                    navigate(`/dashboard/convenzioni/${c.id}/overview`);
                  }}
                  data-testid={`btn-enter-${c.id}`}
                  className="gap-1 bg-emerald-600 hover:bg-emerald-700"
                  disabled={activeConvenzione?.id === c.id}
                >
                  <LogIn className="w-3.5 h-3.5" />
                  {activeConvenzione?.id === c.id ? 'In Gestione' : 'Entra in Gestione'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`btn-edit-${c.id}`} disabled={!isSuperAdmin} className="gap-1">
                  <Edit3 className="w-3.5 h-3.5" /> Modifica
                </Button>
                {isSuperAdmin && (
                  <label className={`inline-flex items-center px-3 py-1.5 text-xs rounded-md border cursor-pointer hover:bg-slate-50 gap-1 ${uploadingLogo === c.id ? 'opacity-50' : ''}`}>
                    {uploadingLogo === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    Logo
                    <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden" onChange={e => handleLogoUpload(c.id, e.target.files?.[0])} data-testid={`input-logo-${c.id}`} />
                  </label>
                )}
                {isSuperAdmin && !c.is_default && (
                  <Button size="sm" variant="outline" onClick={() => handleDelete(c.id)} data-testid={`btn-delete-${c.id}`} className="gap-1 text-red-600 hover:text-red-700">
                    <Trash2 className="w-3.5 h-3.5" /> Elimina
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialog.open} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent data-testid="convenzione-dialog">
          <DialogHeader>
            <DialogTitle>{dialog.mode === 'create' ? 'Nuova Convenzione' : 'Modifica Convenzione'}</DialogTitle>
            <DialogDescription>Compila i dati della convenzione</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome Convenzione *</Label>
              <Input
                value={dialog.form.nome}
                onChange={e => setDialog({ ...dialog, form: { ...dialog.form, nome: e.target.value } })}
                placeholder="Es. Confesercenti"
                data-testid="input-nome"
              />
            </div>
            <div>
              <Label>Codice Univoco {dialog.mode === 'create' ? '(opzionale, generato se vuoto)' : ''}</Label>
              <Input
                value={dialog.form.codice_univoco}
                onChange={e => setDialog({ ...dialog, form: { ...dialog.form, codice_univoco: e.target.value.toUpperCase() } })}
                placeholder="Es. CONF2026"
                disabled={dialog.mode === 'edit' && dialog.form.is_default}
                data-testid="input-codice"
              />
              <p className="text-[11px] text-slate-500 mt-1">A-Z, 0-9, _, - (4-20 caratteri)</p>
            </div>
            <div>
              <Label>Referente Commerciale (opzionale)</Label>
              <Select
                value={dialog.form.top_agent_id || 'none'}
                onValueChange={v => setDialog({ ...dialog, form: { ...dialog.form, top_agent_id: v === 'none' ? '' : v } })}
              >
                <SelectTrigger data-testid="select-top-agent">
                  <SelectValue placeholder="Nessuno (Sede diretta)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nessuno (Sede diretta) —</SelectItem>
                  {agents.map(a => (
                    <SelectItem key={a.id} value={a.id} data-testid={`option-referente-${a.id}`}>
                      {a.name} ({a.email}) — {a.role === 'agente' ? 'Agente' : a.role === 'top_agent' ? 'Top Agent' : a.role === 'admin' ? 'Admin' : 'Super Admin'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">Puoi selezionare qualsiasi utente attivo (Agente, Top Agent, Admin).</p>
            </div>
            <div>
              <Label>Descrizione (opzionale)</Label>
              <Textarea
                value={dialog.form.descrizione}
                onChange={e => setDialog({ ...dialog, form: { ...dialog.form, descrizione: e.target.value } })}
                placeholder="Note interne, ambito, note commerciali..."
                data-testid="input-descrizione"
              />
            </div>
            <div className="border-t pt-3 space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Margini Target Convenzione (%)</Label>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Nuovo</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={dialog.form.target_margin_nuovo}
                    onChange={e => setDialog({ ...dialog, form: { ...dialog.form, target_margin_nuovo: e.target.value } })}
                    data-testid="input-margin-nuovo"
                  />
                </div>
                <div>
                  <Label className="text-xs">Usato</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={dialog.form.target_margin_usato}
                    onChange={e => setDialog({ ...dialog, form: { ...dialog.form, target_margin_usato: e.target.value } })}
                    data-testid="input-margin-usato"
                  />
                </div>
                <div>
                  <Label className="text-xs">4vantage</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={dialog.form.target_margin_4vantage}
                    onChange={e => setDialog({ ...dialog, form: { ...dialog.form, target_margin_4vantage: e.target.value } })}
                    data-testid="input-margin-4vantage"
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-500">
                Margine target usato dal motore di calcolo del <strong>Cliente Inbound</strong> (Task 2.1). Nessun impatto sul catalogo agenti.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving || !dialog.form.nome} data-testid="btn-save-convenzione">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConvenzioniAdminPage;
